import ServicePageHero from "../components/ServicePageHero";
import { openConsultationMail } from "../config/contact";
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowUpRight, FiPlus, FiChevronDown, FiChevronUp, FiTarget, FiMail, FiSearch, FiZap } from "react-icons/fi";
import { Helmet } from "react-helmet-async";
import ServiceDetailSection from "../components/ServiceDetailSection";
import ServiceIntroSection from "../components/ServiceIntroSection";
import img_blog_post_amico from "../assets/Blog post-amico.png";
import img_marketing_consulting_amico from "../assets/Marketing consulting-amico.png";
import img_marketing_consulting_bro from "../assets/Marketing consulting-bro.png";
import img_marketing_consulting_rafiki from "../assets/Marketing consulting-rafiki.png";
import img_marketing_bro_1 from "../assets/Marketing-bro (1).png";
import img_vision_board_rafiki from "../assets/Vision board-rafiki.png";
import img_about_img1 from "../assets/Choose-bro.png";
import img_about_img2 from "../assets/Starting a business project-amico.png";

// Import Content Marketing specific assets

const FAQ_DATA = [
  {
    question: "1. What is content marketing?",
    answer: "Content marketing involves creating and sharing valuable content to attract and engage your target audience."
  },
  {
    question: "2. How does content marketing help my business?",
    answer: "It helps improve visibility, build trust, and generate leads through relevant and useful content."
  },
  {
    question: "3. What type of content do you create?",
    answer: "We create blogs, website content, social media content, and more based on your business needs."
  },
  {
    question: "4. How long does it take to see results?",
    answer: "Content marketing takes time, but consistent efforts lead to long-term growth and better visibility."
  }
];

const scrollReveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemFade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};


const WHY_ICONS = [
  <FiTarget />,
  <FiMail />,
  <FiSearch />,
  <FiZap />,
];
const ContentMarketingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeWhy, setActiveWhy] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const scrollToContact = openConsultationMail; // replaced

  return (
    <>
      <Helmet>
        <title>Content Marketing Services Company | Content Experts India</title>
        <meta name="description" content="Improve your online presence with content marketing services. Hire a content marketing company in India for high-quality content and strategy." />
      </Helmet>
      <main className="content-marketing-page">
      <ServicePageHero
        badge="Our Services"
        title="Best Content Marketing Services"
        subtitle="Create valuable content that attracts your audience and supports your business growth."
        breadcrumb="Home > Services > Content Marketing"
        image={img_marketing_consulting_amico}
      />


      {/* ABOUT SECTION */}
      <ServiceIntroSection
        heading="How Content Marketing Drives Business Growth"
        imageLeft={img_marketing_consulting_bro}
        imageRight={img_blog_post_amico}
        paragraph1="Content marketing helps your business attract, engage, and convert your target audience through valuable and relevant content."
        paragraph2="As a results-focused content marketing agency, we focus on creating content that improves visibility, builds trust, and supports your overall marketing strategy."
      />

      <ServiceDetailSection
        isReversed={false}
        label="Our Content Marketing Strategy"
        title="Content Planning & Strategy"
        description="We create a content strategy based on your business goals and target audience. This ensures your content is relevant and aligned with your brand."
        longDescription="With proper planning, your content delivers consistent value and supports long-term growth."
        image={img_marketing_consulting_rafiki}
        listItems={[
          "Target Audience Analysis",
          "Content Gap Identification",
          "Content Calendar Planning",
        ]}
      />

      <ServiceDetailSection
        isReversed={true} bgLight
        title="Content Creation & Optimization"
        description="We create high-quality content that is informative, engaging, and optimized for search engines. This helps improve visibility and attract the right audience."
        longDescription="Optimized content performs better and supports your overall SEO efforts."
        image={img_marketing_bro_1}
      />

      <ServiceDetailSection
        isReversed={false}
        title="Content Distribution"
        description="We ensure your content reaches the right audience through the right channels. This helps increase visibility and engagement."
        longDescription="Proper distribution helps your content deliver better results."
        image={img_vision_board_rafiki}
      />

      <ServiceDetailSection
        isReversed={true} bgLight
        title="Performance Tracking & Improvement"
        description="We track content performance and analyze results to understand what works best. This helps in improving future content strategies."
        longDescription="Continuous improvement ensures consistent growth and better outcomes."
        image={img_vision_board_rafiki}
      />

      {/* WHY CHOOSE US */}
      <section className="dark-why-section">
        <div className="container-custom">
          <motion.div className="dark-why-header" {...scrollReveal}>
            <span className="dark-why-badge">Why Choose Us</span>
            <h2>Why Businesses Trust Our Content Marketing Expertise</h2>
          </motion.div>

          <motion.div 
            className="dark-why-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, amount: 0.1 }}
          >
            {/* Left: Accordion List */}
            <motion.div className="dark-why-list" variants={itemFade}>
              {[
                { 
                  title: "Strategic Content Approach", 
                  desc: "We create content strategies aligned with your business goals and audience needs. This ensures your content delivers real value. A strategic approach helps in building long-term brand presence." 
                },
                { 
                  title: "Focus on Quality Content", 
                  desc: "We focus on creating high-quality content that is relevant and engaging. This helps attract and retain your audience. Quality content improves trust and supports better results." 
                },
                { 
                  title: "SEO-Driven Content", 
                  desc: "Our content is optimized for search engines to improve visibility and rankings. This helps drive organic traffic to your website. SEO-focused content supports long-term growth." 
                },
                { 
                  title: "Consistent Content Delivery", 
                  desc: "We maintain consistency in content creation and publishing. This helps keep your audience engaged and your brand active. Consistency is key to building a strong online presence." 
                }
              ].map((item, idx) => (
                <div 
                  key={idx} 
                  className={`dark-why-item ${activeWhy === idx ? 'active' : ''}`}
                  onMouseEnter={() => setActiveWhy(idx)}
                >
                  <div className="dark-why-item-header">
                    <span className="dark-why-item-icon-left">{WHY_ICONS[idx]}</span>
                    <h4>{idx + 1}. {item.title}</h4>
                    <span className="dark-why-item-icon">
                      {activeWhy === idx ? <FiArrowUpRight /> : <FiPlus />}
                    </span>
                  </div>
                  <div className="dark-why-item-content">
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Right: Visual Ovals */}
            <motion.div className="dark-why-visual" variants={itemFade}>
              <img src={img_about_img1} alt="Expertise 1" className="dark-oval-1" />
              <img src={img_about_img2} alt="Expertise 2" className="dark-oval-2" />
              <span className="dark-deco-plus"><FiPlus /></span>
              <span className="dark-deco-star">✷</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="faq-section py-20">
        <div className="container-custom max-w-4xl">
          <motion.h2 className="section-title text-center mb-16" {...scrollReveal}>Frequently Asked Questions</motion.h2>
          <motion.div 
            className="faq-list"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, amount: 0.1 }}
          >
            {FAQ_DATA.map((faq, index) => (
              <motion.div 
                key={index} 
                className={`faq-item ${openFaq === index ? 'active' : ''}`}
                variants={itemFade}
              >
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  <span>{faq.question}</span>
                  {openFaq === index ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div 
                      className="faq-answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="service-page-cta service-page-cta--soft py-20">
        <div className="container-custom">
          <motion.div 
            className="service-page-cta-inner text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Build Your Brand with Effective Content Marketing</h2>
            <p className="mb-8">Our content marketing services in Delhi NCR are designed to improve visibility, engage your audience, and support business growth.</p>
            <button className="nav-btn mx-auto" onClick={scrollToContact} style={{ padding: '14px 32px', fontSize: '16px' }}>
              Get Free Consultation
              <span className="nav-btn-arrow" style={{ width: '28px', height: '28px' }}>
                <FiArrowUpRight />
              </span>
            </button>
          </motion.div>
        </div>
      </section>
      </main>
    </>
  );
};

export default ContentMarketingPage;
